import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { getEinsatzContent } from './einsatzContent';
import { useAuth } from '../../contexts/AuthContext';
import { canViewDeepDocument, getEinsatzMenuItems } from '../../config/roleAccess';
import RoleGate from '../../components/RoleGate';
import { getDeepDocument, getDeepFile } from '../../api/einsatzDocuments';
import { downloadPdfResponse, extractPdfErrorMessage } from '../../utils/pdfDownload';

function useDocumentImage(src) {
  const protectedImage = src?.startsWith('/api/einsatz-documents/');
  const [image, setImage] = useState({ source: null, url: null, error: null });
  useEffect(() => {
    if (!protectedImage) return undefined;
    const controller = new AbortController();
    let objectUrl;
    getDeepFile(src, controller.signal).then((response) => {
      if (controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(response.data);
      setImage({ source: src, url: objectUrl, error: null });
    }).catch((error) => {
      if (controller.signal.aborted) return;
      console.error('DEEP-Bild konnte nicht geladen werden.');
      setImage({ source: src, url: null, error: 'Bild konnte nicht geladen werden.' });
    });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, protectedImage]);
  if (!protectedImage) return { url: src };
  return {
    ...(image.source === src ? image : { url: null, error: null }),
    onError: () => {
      console.error('DEEP-Bild konnte nicht dargestellt werden.');
      setImage({ source: src, url: null, error: 'Bild konnte nicht dargestellt werden.' });
    },
  };
}

function SectionList({ items }) {
  return (
    <List dense sx={{ py: 0 }}>
      {items.map((item) => (
        <ListItem key={item} disableGutters sx={{ display: 'list-item', ml: 2 }}>
          <ListItemText primary={item} sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }} />
        </ListItem>
      ))}
    </List>
  );
}

function GroupSection({ section }) {
  return (
    <Stack spacing={2}>
      {section.intro ? <Typography color="text.secondary">{section.intro}</Typography> : null}
      {section.groups.map((group) => (
        <Card key={group.title} variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {group.title}
            </Typography>
            <SectionList items={group.items} />
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function MediaSection({ section, onOpenMedia }) {
  const image = useDocumentImage(section.media.src);
  const displayMedia = { ...section.media, src: image.url };
  const inlineTitle = Object.prototype.hasOwnProperty.call(section.media, 'inlineTitle')
    ? section.media.inlineTitle
    : section.media.title;
  const inlineLabel = Object.prototype.hasOwnProperty.call(section.media, 'inlineLabel')
    ? section.media.inlineLabel
    : section.media.label;

  return (
    <Stack spacing={2}>
      {section.description ? <Typography color="text.secondary">{section.description}</Typography> : null}

      {Array.isArray(section.highlights) && section.highlights.length > 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Besondere Hinweise
            </Typography>
            <SectionList items={section.highlights} />
          </CardContent>
        </Card>
      ) : null}

      <Card variant="outlined" sx={{ width: '100%', maxWidth: section.media.maxWidth }}>
        <CardContent>
          {inlineTitle || inlineLabel ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
              {inlineTitle ? (
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {inlineTitle}
                </Typography>
              ) : null}
              {inlineLabel ? <Chip size="small" label={inlineLabel} /> : null}
            </Stack>
          ) : null}
          {section.media.src ? (
            <Stack spacing={1.25}>
              {image.error ? <Alert severity="error">{image.error}</Alert> : !image.url ? (
                <CircularProgress size={24} aria-label="Bild wird geladen" />
              ) : <Box
                component="img"
                src={image.url}
                onError={image.onError}
                alt={section.media.alt || section.media.title}
                onClick={() => onOpenMedia(displayMedia)}
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                  backgroundColor: '#fff',
                  cursor: 'zoom-in',
                }}
              />}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<OpenInFullIcon />}
                  disabled={!image.url}
                  onClick={() => onOpenMedia(displayMedia)}
                >
                  Vollbild und Zoom
                </Button>
              </Stack>
              {section.media.note ? (
                <Typography variant="body2" color="text.secondary">
                  {section.media.note}
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Alert severity="info">{section.media.note}</Alert>
          )}
        </CardContent>
      </Card>

      {Array.isArray(section.callouts)
        ? section.callouts.map((callout) => (
        <Card key={callout.title} variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {callout.title}
            </Typography>
            <SectionList items={callout.items} />
          </CardContent>
        </Card>
          ))
        : null}
    </Stack>
  );
}

function TextSection({ section }) {
  return (
    <Stack spacing={1.5}>
      {section.heading ? (
        <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 700 }}>
          {section.heading}
        </Typography>
      ) : null}
      {section.paragraphs.map((paragraph) => {
        const text = typeof paragraph === 'string' ? paragraph : paragraph.text;
        return (
          <Typography key={text} color="text.secondary" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
            {paragraph.strong ? <Box component="strong" sx={{ color: paragraph.color || 'text.primary' }}>{text}</Box> : text}
          </Typography>
        );
      })}
    </Stack>
  );
}

function NestedSectionList({ sections, expandedSections, onToggleSection, onOpenMedia }) {
  return (
    <Stack spacing={1.5}>
      {sections.map((section) => (
        <Accordion
          key={section.id}
          expanded={expandedSections.includes(section.id)}
          onChange={onToggleSection(section.id)}
          disableGutters
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {section.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderSectionContent(section, {
              expandedSections,
              onToggleSection,
              onOpenMedia,
            })}
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}

function renderSectionContent(section, { expandedSections, onToggleSection, onOpenMedia }) {
  if (section.layout === 'illustrated') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 0.85fr) minmax(0, 1.4fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <MediaSection section={{ media: section.media }} onOpenMedia={onOpenMedia} />
        <Box sx={{ minWidth: 0 }}>
          {renderSectionContent({ blocks: section.blocks }, { expandedSections, onToggleSection, onOpenMedia })}
        </Box>
      </Box>
    );
  }

  if (section.mediaGallery) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        {section.mediaGallery.map((media) => (
          <MediaSection key={media.src} section={{ media }} onOpenMedia={onOpenMedia} />
        ))}
      </Box>
    );
  }

  if (Array.isArray(section.blocks) && section.blocks.length > 0) {
    return (
      <Stack spacing={2}>
        {section.blocks.map((block, index) => (
          <Box key={block.id || `${section.id || 'block'}-${index}`}>
            {renderSectionContent(block, {
              expandedSections,
              onToggleSection,
              onOpenMedia,
            })}
          </Box>
        ))}
      </Stack>
    );
  }

  if (Array.isArray(section.children) && section.children.length > 0) {
    return (
      <Stack spacing={2}>
        {section.intro ? <Typography color="text.secondary">{section.intro}</Typography> : null}
        {section.paragraphs ? <TextSection section={section} /> : null}
        <NestedSectionList
          sections={section.children}
          expandedSections={expandedSections}
          onToggleSection={onToggleSection}
          onOpenMedia={onOpenMedia}
        />
      </Stack>
    );
  }

  if (section.groups) {
    return <GroupSection section={section} />;
  }

  if (section.media) {
    return <MediaSection section={section} onOpenMedia={onOpenMedia} />;
  }

  if (section.paragraphs) {
    return <TextSection section={section} />;
  }

  if (section.links) {
    return (
      <Stack spacing={1} alignItems="flex-start">
        {section.links.map((link) => (
          <Button key={link.href} component="a" href={link.href} target="_blank" rel="noopener noreferrer" variant="outlined">
            {link.label}
          </Button>
        ))}
      </Stack>
    );
  }

  return null;
}

function EinsatzPage() {
  const { user } = useAuth();
  const theme = useTheme();
  const fullScreenMedia = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const type = (params.get('type') || '').toLowerCase();
  const deepAllowed = canViewDeepDocument(user);
  const menuItems = getEinsatzMenuItems(user);
  const currentMenuPath = useMemo(() => {
    return type ? `/einsatz?type=${type}` : false;
  }, [type]);
  const [deepState, setDeepState] = useState({ content: null, error: null, loading: true });
  const content = type === 'deep'
    ? (deepAllowed ? deepState.content : null)
    : getEinsatzContent(type);
  const [downloadState, setDownloadState] = useState({ loading: false, error: null });
  const downloadController = useRef(null);
  const [mediaDialog, setMediaDialog] = useState({ open: false, media: null, zoom: 1 });
  const [expandedSections, setExpandedSections] = useState([]);

  useEffect(() => {
    setExpandedSections([]);
    setMediaDialog({ open: false, media: null, zoom: 1 });
    setDownloadState({ loading: false, error: null });
  }, [content]);

  useEffect(() => () => downloadController.current?.abort(), [type, deepAllowed]);

  useEffect(() => {
    if (type !== 'deep' || !deepAllowed) return undefined;
    const controller = new AbortController();
    setDeepState({ content: null, error: null, loading: true });
    getDeepDocument(controller.signal).then(({ data }) => {
      if (!controller.signal.aborted) setDeepState({ content: data, error: null, loading: false });
    }).catch((error) => {
      if (controller.signal.aborted) return;
      console.error('DEEP-Datenblatt konnte nicht geladen werden.');
      setDeepState({
        content: null,
        error: error.response?.data?.error || 'DEEP-Datenblatt konnte nicht geladen werden.',
        loading: false,
      });
    });
    return () => controller.abort();
  }, [type, deepAllowed]);

  const handleDownload = async () => {
    downloadController.current?.abort();
    const controller = new AbortController();
    downloadController.current = controller;
    setDownloadState({ loading: true, error: null });
    try {
      const response = await getDeepFile(content.referenceDocument.href, controller.signal);
      if (controller.signal.aborted) return;
      downloadPdfResponse(response, content.referenceDocument.filename);
      setDownloadState({ loading: false, error: null });
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('DEEP-PDF konnte nicht heruntergeladen werden.');
      const message = await extractPdfErrorMessage(error, 'PDF konnte nicht heruntergeladen werden.');
      if (controller.signal.aborted) return;
      setDownloadState({
        loading: false,
        error: message,
      });
    }
  };

  const handleToggleSection = (sectionId) => (_event, isExpanded) => {
    setExpandedSections((current) => {
      if (isExpanded) {
        return [...current, sectionId];
      }

      return current.filter((item) => item !== sectionId);
    });
  };

  const handleOpenMedia = (media) => {
    setMediaDialog({ open: true, media, zoom: 1 });
  };

  const handleCloseMedia = () => {
    setMediaDialog({ open: false, media: null, zoom: 1 });
  };

  const handleZoomIn = () => {
    setMediaDialog((current) => ({
      ...current,
      zoom: Math.min(current.zoom + 0.25, 3),
    }));
  };

  const handleZoomOut = () => {
    setMediaDialog((current) => ({
      ...current,
      zoom: Math.max(current.zoom - 0.25, 0.75),
    }));
  };

  const handleZoomReset = () => {
    setMediaDialog((current) => ({
      ...current,
      zoom: 1,
    }));
  };

  if (type === 'deep' && !deepAllowed) {
    return <RoleGate check={canViewDeepDocument}><></></RoleGate>;
  }

  return (
    <>
      <Box sx={{ px: { xs: 1, sm: 3 }, py: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Einsatz
            </Typography>
          </Box>

          <Card>
            <CardContent>
              <Stack spacing={1.25}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Navigation
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  aria-label="Einsatztypen Navigation"
                >
                  {menuItems.map((item) => (
                    <Button
                      key={item.path}
                      variant={item.path === currentMenuPath ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => {
                        if (item.path !== currentMenuPath) {
                          navigate(item.path);
                        }
                      }}
                      aria-label={`Zu ${item.label} wechseln`}
                      aria-current={item.path === currentMenuPath ? 'page' : undefined}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              {content ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {content.subtitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {content.source}
                    </Typography>
                  </Box>

                  {content.referenceDocument ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      {type === 'deep' ? (
                        <Button variant="outlined" onClick={handleDownload} disabled={downloadState.loading}>
                          {downloadState.loading ? 'PDF wird geladen …' : content.referenceDocument.label}
                        </Button>
                      ) : <Button
                        component="a"
                        href={content.referenceDocument.href}
                        download={content.referenceDocument.filename}
                        variant="outlined"
                      >
                        {content.referenceDocument.label}
                      </Button>}
                    </Stack>
                  ) : null}
                  {downloadState.error ? <Alert severity="error">{downloadState.error}</Alert> : null}

                  {Array.isArray(content.quickFacts) && content.quickFacts.length > 0 ? (
                    <Card variant="outlined">
                      <CardContent>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1.5 }}>
                          <WarningAmberIcon color="warning" />
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Schnellübersicht
                          </Typography>
                        </Stack>
                        <SectionList items={content.quickFacts} />
                      </CardContent>
                    </Card>
                  ) : null}

                  <Divider />

                  <Stack spacing={1.5}>
                    {content.sections.map((section) => (
                      <Accordion
                        key={section.id}
                        expanded={expandedSections.includes(section.id)}
                        onChange={handleToggleSection(section.id)}
                        disableGutters
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {section.title}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {renderSectionContent(section, {
                            expandedSections,
                            onToggleSection: handleToggleSection,
                            onOpenMedia: handleOpenMedia,
                          })}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Stack>
                </Stack>
              ) : type === 'deep' ? (
                deepState.loading
                  ? <CircularProgress aria-label="DEEP-Datenblatt wird geladen" />
                  : <Alert severity="error">{deepState.error}</Alert>
              ) : (
                <Alert severity="info">
                  Für diesen Einsatztyp ist die strukturierte Fachansicht noch nicht hinterlegt. Die Route bleibt aktiv, damit Navigation und Berechtigungen bereits funktionieren.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Dialog
        open={mediaDialog.open}
        onClose={handleCloseMedia}
        fullScreen={fullScreenMedia}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <Stack spacing={0} sx={{ height: fullScreenMedia ? '100vh' : '90vh', bgcolor: '#111' }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.5, py: 1, color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.12)' }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {mediaDialog.media?.title}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                  Zoom: {Math.round(mediaDialog.zoom * 100)}%
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Verkleinern">
                  <span>
                    <IconButton aria-label="Zoom verkleinern" onClick={handleZoomOut} sx={{ color: '#fff' }} disabled={mediaDialog.zoom <= 0.75}>
                      <ZoomOutIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Zurücksetzen">
                  <IconButton aria-label="Zoom zurücksetzen" onClick={handleZoomReset} sx={{ color: '#fff' }}>
                    <RestartAltIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Vergrößern">
                  <span>
                    <IconButton aria-label="Zoom vergrößern" onClick={handleZoomIn} sx={{ color: '#fff' }} disabled={mediaDialog.zoom >= 3}>
                      <ZoomInIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Schließen">
                  <IconButton aria-label="Bildansicht schließen" onClick={handleCloseMedia} sx={{ color: '#fff' }}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', justifyContent: 'center' }}>
              {mediaDialog.media?.src ? (
                <Box
                  component="img"
                  src={mediaDialog.media.src}
                  alt={mediaDialog.media.alt || mediaDialog.media.title}
                  sx={{
                    width: `${mediaDialog.zoom * 100}%`,
                    maxWidth: 'none',
                    height: 'auto',
                    alignSelf: 'flex-start',
                    borderRadius: 1,
                    backgroundColor: '#fff',
                  }}
                />
              ) : null}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EinsatzPage;
